<script setup lang="ts">
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useInjectSheetMusic } from '@/composables/useSheetMusic';
import { useResizeObserver } from '@vueuse/core';
import { computed, ref } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import type { Id } from '../../convex/_generated/dataModel';
import { getGradeForAccuracy, getAccuracyLabel } from '@/utils/grade';
import GradeBadge from './GradeBadge.vue';
import GameAreaHighScoreRow from './GameAreaHighScoreRow.vue';
import BaseButton from './BaseButton.vue';
import IconButton from './IconButton.vue';

defineProps<{
  musicalPieceId?: Id<'musicalPieces'>;
}>();

const gameEngine = useInjectGameEngine();
const sheetMusic = useInjectSheetMusic();
const emit = defineEmits<{
  (e: 'exit'): void;
  (e: 'close'): void;
}>();

const performanceSummary = ref<HTMLElement>();
const performanceSummaryWidth = ref(0);

const accuracyLabel = computed(() => getAccuracyLabel(gameEngine.accuracy.value));
const grade = computed(() =>
  getGradeForAccuracy(gameEngine.accuracy.value, !gameEngine.targetLineVisibleDuringRun.value),
);

useResizeObserver(performanceSummary, (entries) => {
  const entry = entries[0];
  performanceSummaryWidth.value = entry.contentRect.width;
});

const handlePlayAgain = async () => {
  gameEngine.initGame();
  await gameEngine.startGame();
};

const handleExit = () => {
  emit('exit');
};
</script>

<template>
  <div
    class="fixed inset-0 bg-background/90 flex items-center justify-center z-50 p-4 w-(--screen-width) h-(--screen-height)"
  >
    <div class="bg-surface rounded-xl max-w-3xl w-full p-6 shadow-xl relative overflow-hidden">
      <div class="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2">
        <div class="w-48 h-48 rounded-full bg-primary/50" />
      </div>

      <IconButton
        class="absolute top-3 right-3 z-10"
        variant="text"
        size="sm"
        aria-label="Close"
        @click="emit('close')"
      >
        <XMarkIcon class="w-5 h-5" />
      </IconButton>

      <div class="mb-6">
        <h2 class="text-2xl font-bold text-text-primary">
          Game Results
        </h2>
        <p class="text-text-secondary">
          {{ sheetMusic.songTitle.value || 'Untitled score' }}
        </p>
      </div>

      <div class="flex flex-row gap-6 mb-6 flex-wrap">
        <!-- Left: grade + label -->
        <div class="flex-grow flex flex-col items-center justify-center gap-2">
          <span class="stamp-in">
            <GradeBadge
              :grade="grade"
              size="xl"
            />
          </span>
          <p class="text-text-primary text-lg font-semibold text-center">
            {{ accuracyLabel }}
          </p>
        </div>

        <!-- Middle: stats -->
        <div class="flex-grow space-y-3 min-w-48">
          <div class="flex justify-between items-center">
            <span class="text-text-secondary">Score</span>
            <span class="text-text-primary font-bold">{{ gameEngine.score.value }}</span>
          </div>

          <div class="flex justify-between items-center">
            <span class="text-text-secondary">Accuracy</span>
            <span class="text-text-primary font-bold">{{ Math.floor(gameEngine.accuracy.value) }}%</span>
          </div>

          <div class="flex justify-between items-center">
            <span class="text-text-secondary">Notes Hit</span>
            <span class="text-text-primary">{{ gameEngine.hitNotes.value }} / {{ gameEngine.totalNotes.value }}</span>
          </div>

          <div class="flex justify-between items-center">
            <span class="text-text-secondary">Longest Combo</span>
            <span class="text-text-primary font-bold">{{ gameEngine.longestCombo.value }}</span>
          </div>

          <GameAreaHighScoreRow
            v-if="musicalPieceId"
            :musical-piece-id="musicalPieceId"
          />
        </div>

        <!-- Right: per-status counts -->
        <div
          ref="performanceSummary"
          class="flex-grow flex justify-center gap-2 min-w-24 flex-wrap"
          :class="{'flex-col': performanceSummaryWidth < 255}"
        >
          <div class="grow bg-primary/10 rounded-lg p-2 text-center">
            <div class="text-green-400 font-bold">
              {{ gameEngine.perfectCount.value }}
            </div>
            <div class="text-text-secondary text-xs">
              Perfect
            </div>
          </div>

          <div class="grow bg-primary/10 rounded-lg p-2 text-center">
            <div class="text-gold font-bold">
              {{ gameEngine.goodCount.value }}
            </div>
            <div class="text-text-secondary text-xs">
              Good
            </div>
          </div>

          <div class="grow bg-primary/10 rounded-lg p-2 text-center">
            <div class="text-red-400 font-bold">
              {{ gameEngine.missedCount.value }}
            </div>
            <div class="text-text-secondary text-xs">
              Missed
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="musicalPieceId && !gameEngine.isHighscoreEligible.value"
        role="status"
        class="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/15 text-text-primary text-sm"
      >
        <ExclamationTriangleIcon class="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
        <span>This run did not count toward your highscore (paused, looped, or below full speed).</span>
      </div>

      <slot name="actions">
        <div class="flex gap-3">
          <BaseButton
            class="flex-1 py-3 justify-center font-semibold"
            color="primary"
            @click="handlePlayAgain"
          >
            Play Again
          </BaseButton>
          <BaseButton
            class="flex-1 py-3 justify-center font-semibold"
            color="surface"
            @click="handleExit"
          >
            Back to Songs
          </BaseButton>
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.stamp-in {
  display: inline-block;
  animation:
    stamp-in-crash 500ms cubic-bezier(0.7, 0, 0.84, 0) 0ms both;
}

@keyframes stamp-in-crash {
  from { opacity: 0; transform: translate(48px, -40px) scale(2.6); }
  to   { opacity: 1; transform: translate(0, 0) scale(1); }
}
</style>
